import { useState, useMemo } from 'react'
import {
  Building2,
  TrendingUp,
  ClipboardCheck,
  Percent,
  ChevronRight,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  GitBranch,
} from 'lucide-react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts'
import { useModuleDemoEnabled, useModuleLoans, useValuationRecords } from '@/hooks/useDemoDataAccess'
import { ANTARCTICA_DEALS } from '@/data/clientMockData'
import type { ValuationRecord } from '@/types'
import { deriveValuationRecords, safeAverage } from '@/pages/liveModuleData'
import {
  CHART_TOOLTIP_PROPS,
  chartAxisProps,
  chartBenchmarkBarColor,
  chartGridProps,
  chartPassFailColor,
} from '@/config/chartTheme'

// ---------------------------------------------------------------------------
// Lifecycle stage definitions (Item 7)
// ---------------------------------------------------------------------------

const LIFECYCLE_STAGES = [
  { id: 'pre_deal', label: 'Pre-Deal', short: 'Pre-Deal' },
  { id: 'term_sheet', label: 'Term Sheet', short: 'Term Sheet' },
  { id: 'commitment', label: 'Commitment', short: 'Commit' },
  { id: 'in_closing', label: 'In Closing', short: 'Closing' },
  { id: 'post_closing', label: 'Post-Close', short: 'Post-Close' },
  { id: 'annual_review', label: 'Annual Review', short: 'Annual' },
]

// Mock lifecycle data per property
interface LifecycleStageRecord {
  stage: string
  appraised_value: number     // $MM
  cap_rate: number
  ltv: number
  dscr: number
  noi: number
  status: 'Finalized' | 'In Review' | 'Draft' | 'N/A'
  effective_date: string
  variance_pct?: number
  variance_reasons?: string[]
}

const LIFECYCLE_DATA: Record<string, LifecycleStageRecord[]> = {
  'ALG-1013': [
    { stage: 'pre_deal', appraised_value: 84.0, cap_rate: 5.95, ltv: 0.72, dscr: 1.38, noi: 5.0, status: 'Finalized', effective_date: '2025-08-15' },
    { stage: 'term_sheet', appraised_value: 86.5, cap_rate: 5.80, ltv: 0.70, dscr: 1.41, noi: 5.02, status: 'Finalized', effective_date: '2025-09-10', variance_pct: 2.98, variance_reasons: ['Rent rolls showed stronger-than-projected occupancy', 'Cap rate tightened on market comps'] },
    { stage: 'commitment', appraised_value: 87.2, cap_rate: 5.75, ltv: 0.70, dscr: 1.42, noi: 5.02, status: 'Finalized', effective_date: '2025-10-05', variance_pct: 0.81, variance_reasons: ['Minor NOI refinement from FIRREA appraisal'] },
    { stage: 'in_closing', appraised_value: 87.2, cap_rate: 5.75, ltv: 0.70, dscr: 1.42, noi: 5.02, status: 'Finalized', effective_date: '2025-11-20', variance_pct: 0.0 },
    { stage: 'post_closing', appraised_value: 89.1, cap_rate: 5.65, ltv: 0.68, dscr: 1.45, noi: 5.03, status: 'Finalized', effective_date: '2026-01-15', variance_pct: 2.18, variance_reasons: ['Cap rate compression in Scottsdale submarket', 'Occupancy improved to 96.5%'] },
  ],
  'ALG-1014': [
    { stage: 'pre_deal', appraised_value: 58.0, cap_rate: 6.20, ltv: 0.68, dscr: 1.42, noi: 3.6, status: 'Finalized', effective_date: '2025-07-10' },
    { stage: 'term_sheet', appraised_value: 57.5, cap_rate: 6.30, ltv: 0.68, dscr: 1.41, noi: 3.62, status: 'Finalized', effective_date: '2025-08-12', variance_pct: -0.86, variance_reasons: ['Slight cap rate expansion on market comps', 'Lease rollover risk factored in'] },
    { stage: 'commitment', appraised_value: 57.8, cap_rate: 6.25, ltv: 0.68, dscr: 1.42, noi: 3.61, status: 'Finalized', effective_date: '2025-09-20', variance_pct: 0.52 },
    { stage: 'in_closing', appraised_value: 57.8, cap_rate: 6.25, ltv: 0.68, dscr: 1.42, noi: 3.61, status: 'Finalized', effective_date: '2025-10-18', variance_pct: 0.0 },
    { stage: 'post_closing', appraised_value: 59.2, cap_rate: 6.10, ltv: 0.66, dscr: 1.46, noi: 3.61, status: 'In Review', effective_date: '2026-02-01', variance_pct: 2.42, variance_reasons: ['Industrial rents outperformed projections', 'Lease renewal at 8% premium to in-place'] },
  ],
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const fmt = (n: number) =>
  n >= 1000 ? `$${(n / 1000).toFixed(2)}B` : `$${n.toFixed(1)}M`

const pct = (n: number, d = 1) => `${(n * 100).toFixed(d)}%`

// A live valuation row holds 0 where the record has no figure (no appraisal,
// no stabilized value, no discount or terminal rate). Zero is shown as "not
// recorded", never as $0.0M or 0.00%.
const money = (n: number) => (n > 0 ? fmt(n) : '—')
const rate = (n: number) => (n > 0 ? `${n.toFixed(2)}%` : '—')
const share = (n: number, d = 1) => (n > 0 ? pct(n, d) : '—')

const statusBadge = (status: ValuationRecord['status']) => {
  const map: Record<string, string> = {
    'Awaiting Draft': 'bg-amber-400/10 text-amber-400 border-amber-400/20',
    'In Review': 'bg-blue-400/10 text-blue-400 border-blue-400/20',
    'Finalized': 'bg-emerald-400/10 text-emerald-400 border-emerald-400/20',
    'No Appraisal': 'bg-stone-400/10 text-stone-400 border-stone-400/20',
  }
  return map[status] ?? 'bg-stone-400/10 text-stone-400 border-stone-400/20'
}

// ---------------------------------------------------------------------------
// Mock peer benchmark data generator
// ---------------------------------------------------------------------------

function peerData(subject: number, label: string) {
  const spread = subject * 0.18
  const p25 = subject - spread * 0.6
  const median = subject - spread * 0.15
  const p75 = subject + spread * 0.55
  return [
    { name: 'Subject', value: Math.round(subject * 100) / 100, label },
    { name: '25th Pctl', value: Math.round(p25 * 100) / 100, label },
    { name: 'Median', value: Math.round(median * 100) / 100, label },
    { name: '75th Pctl', value: Math.round(p75 * 100) / 100, label },
  ]
}

// ---------------------------------------------------------------------------
// Performance attribution (mock)
// ---------------------------------------------------------------------------

const ATTRIBUTION = [
  { label: 'Income Growth', value: 2.1 },
  { label: 'Cap Rate Compression', value: 1.3 },
  { label: 'Occupancy Change', value: -0.4 },
  { label: 'Market Movement', value: 0.8 },
]

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

type ValFilter = 'all' | 'pre-deal' | 'post-close'
type ValView = 'queue' | 'lifecycle'

export default function Valuation() {
  const demoEnabled = useModuleDemoEnabled('valuation')
  const demoRecords = useValuationRecords('valuation')
  const { loans, isLoading, isLive } = useModuleLoans('valuation', ANTARCTICA_DEALS)
  const valuationRecords = demoEnabled ? demoRecords : deriveValuationRecords(loans)
  const deals = demoEnabled ? ANTARCTICA_DEALS : loans
  const [selectedId, setSelectedId] = useState<string>('')
  const [valFilter, setValFilter] = useState<ValFilter>('all')
  const [valView, setValView] = useState<ValView>('queue')
  const [lifecycleDealId, setLifecycleDealId] = useState<string>('ALG-1013')

  const filteredRecords = valFilter === 'all'
    ? valuationRecords
    : valuationRecords.filter((v) => (v.valuationType ?? 'post-close') === valFilter)

  const selected = useMemo(
    () => valuationRecords.find((v) => v.propertyId === selectedId) ?? valuationRecords[0],
    [selectedId, valuationRecords],
  )

  // Find the deal for extra context
  const deal = useMemo(
    () => (selected ? deals.find((d) => d.id === selected.propertyId) : undefined),
    [deals, selected],
  )

  // Metrics — never divide by an empty queue
  const propertiesValued = valuationRecords.filter((v) => v.status === 'Finalized').length
  const withCapRate = valuationRecords.filter((v) => v.capRate > 0)
  const avgCapRate = safeAverage(withCapRate.map((v) => v.capRate))
  const recordsWithChange = valuationRecords.filter((v) => v.valueChangePct != null)
  const avgValueChange = safeAverage(recordsWithChange.map((v) => v.valueChangePct ?? 0))
  const awaitingReview = valuationRecords.filter((v) => v.status === 'Awaiting Draft' || v.status === 'In Review').length

  const capRateBench = selected ? peerData(selected.capRate, '%') : []
  const valueSf = selected && deal?.sf ? (selected.asIsValue * 1_000_000) / deal.sf : null
  const valueSfBench = valueSf != null ? peerData(valueSf, '$/SF') : []
  const occBench = selected && selected.occupancy > 0
    ? [
        { name: 'Subject', value: Math.round(selected.occupancy * 1000) / 10 },
        ...(demoEnabled ? [{ name: 'Market Avg', value: 91.0 }] : []),
      ]
    : []

  const totalReturn = ATTRIBUTION.reduce((s, a) => s + a.value, 0)

  const lifecycleIds = demoEnabled ? Object.keys(LIFECYCLE_DATA) : []
  const lifecycleRecords = demoEnabled ? (LIFECYCLE_DATA[lifecycleDealId] || []) : []
  const lifecycleDeal = deals.find((d) => d.id === lifecycleDealId)

  if (isLoading) {
    return <div className="px-4 py-3 text-stone-400 text-sm">Loading valuation book…</div>
  }

  return (
    <div className="px-4 py-3 space-y-3">
      {/* ── View Switcher ─────────────────────────────────────────────── */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => setValView('queue')}
          className={`rounded-md px-3 py-1.5 text-[11px] font-medium transition-all border ${
            valView === 'queue' ? 'bg-amber-400/15 text-amber-200 border-amber-400/25' : 'text-stone-500 border-transparent hover:text-stone-300 hover:border-stone-700/50'
          }`}
        >
          Valuation Queue
        </button>
        <button
          onClick={() => setValView('lifecycle')}
          className={`rounded-md px-3 py-1.5 text-[11px] font-medium transition-all border flex items-center gap-1.5 ${
            valView === 'lifecycle' ? 'bg-amber-400/15 text-amber-200 border-amber-400/25' : 'text-stone-500 border-transparent hover:text-stone-300 hover:border-stone-700/50'
          }`}
        >
          <GitBranch size={11} />
          Lifecycle View
        </button>
        {valView === 'queue' && (
          <div className="flex items-center gap-1.5 ml-2">
            {(['all', 'pre-deal', 'post-close'] as ValFilter[]).map((f) => (
              <button
                key={f}
                onClick={() => setValFilter(f)}
                className={`rounded-md px-2.5 py-1 text-[10px] font-medium transition-all ${
                  valFilter === f
                    ? 'bg-stone-800 text-stone-200 border border-stone-600'
                    : 'text-stone-600 hover:text-stone-400 border border-transparent'
                }`}
              >
                {f === 'all' ? 'All' : f === 'pre-deal' ? 'Pre-Deal' : 'Post-Close'}
              </button>
            ))}
            <span className="ml-1 text-[10px] text-stone-600 font-mono">{filteredRecords.length} records</span>
          </div>
        )}
      </div>

      {/* ── LIFECYCLE VIEW (Item 7) ───────────────────────────────────── */}
      {valView === 'lifecycle' && !demoEnabled && (
        <div className="panel">
          <div className="panel-body py-10 text-center text-stone-500 text-[12px]">
            No lifecycle valuations are on file for the live book.
          </div>
        </div>
      )}

      {valView === 'lifecycle' && demoEnabled && (
        <div className="space-y-3">
          {/* Deal selector */}
          <div className="flex items-center gap-3">
            <span className="text-[11px] text-stone-500">Deal:</span>
            <select
              value={lifecycleDealId}
              onChange={(e) => setLifecycleDealId(e.target.value)}
              className="bg-black/40 border border-amber-400/20 rounded-lg px-3 py-1.5 text-[11px] text-stone-200 focus:outline-none focus:border-amber-400/40"
            >
              {lifecycleIds.map((id) => {
                const d = deals.find((x) => x.id === id)
                return <option key={id} value={id}>{id} — {d?.property || id}</option>
              })}
            </select>
            {lifecycleDeal && (
              <span className="text-[10px] text-stone-500">
                {lifecycleDeal.type} &middot; {lifecycleDeal.location}
              </span>
            )}
          </div>

          {/* Side-by-side lifecycle stages */}
          <div className="panel overflow-x-auto">
            <div className="panel-header">
              <div className="flex items-center gap-1.5">
                <GitBranch size={11} className="text-amber-400" />
                <span className="panel-header-title">Valuation Lifecycle — {lifecycleDeal?.property}</span>
              </div>
              <span className="text-[10px] text-stone-600">Side-by-side variance tracking</span>
            </div>
            <div className="panel-body overflow-x-auto">
              <table className="dense-table">
                <thead>
                  <tr>
                    <th className="w-[140px]">Metric</th>
                    {LIFECYCLE_STAGES.map((s) => {
                      const rec = lifecycleRecords.find((r) => r.stage === s.id)
                      return (
                        <th key={s.id} className="text-center">
                          <div className={`text-[10px] ${rec ? 'text-stone-300' : 'text-stone-700'}`}>{s.label}</div>
                          {rec && (
                            <div className={`text-[9px] mt-0.5 ${
                              rec.status === 'Finalized' ? 'text-emerald-400' :
                              rec.status === 'In Review' ? 'text-amber-400' :
                              rec.status === 'Draft' ? 'text-blue-400' : 'text-stone-600'
                            }`}>{rec.status}</div>
                          )}
                          {rec && <div className="text-[8px] text-stone-600">{rec.effective_date}</div>}
                        </th>
                      )
                    })}
                  </tr>
                </thead>
                <tbody>
                  {[
                    { label: 'Appraised Value', key: 'appraised_value', format: (v: number) => fmt(v), isMain: true },
                    { label: 'Cap Rate', key: 'cap_rate', format: (v: number) => `${v.toFixed(2)}%` },
                    { label: 'LTV', key: 'ltv', format: (v: number) => `${(v*100).toFixed(1)}%` },
                    { label: 'DSCR', key: 'dscr', format: (v: number) => `${v.toFixed(2)}x` },
                    { label: 'NOI', key: 'noi', format: (v: number) => `$${v.toFixed(1)}M` },
                  ].map(({ label, key, format, isMain }) => (
                    <tr key={key}>
                      <td className="text-stone-400 font-medium">{label}</td>
                      {LIFECYCLE_STAGES.map((s) => {
                        const rec = lifecycleRecords.find((r) => r.stage === s.id)
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- dynamic metric key into a typed record
                        const val = rec ? (rec as any)[key] : null
                        return (
                          <td key={s.id} className={`text-center tabular-nums ${isMain ? 'text-amber-300 font-semibold' : 'text-stone-300'} ${!rec ? 'text-stone-700' : ''}`}>
                            {val != null ? format(val) : <span className="text-stone-700">—</span>}
                          </td>
                        )
                      })}
                    </tr>
                  ))}
                  {/* Variance row */}
                  <tr className="border-t border-amber-400/10">
                    <td className="text-stone-500">Δ vs Prior Stage</td>
                    {LIFECYCLE_STAGES.map((s) => {
                      const rec = lifecycleRecords.find((r) => r.stage === s.id)
                      const vPct = rec?.variance_pct
                      return (
                        <td key={s.id} className="text-center">
                          {vPct != null ? (
                            <span className={`flex items-center justify-center gap-0.5 text-[10px] font-medium ${
                              vPct > 0 ? 'text-emerald-400' : vPct < 0 ? 'text-red-400' : 'text-stone-500'
                            }`}>
                              {vPct > 0 ? <ArrowUpRight size={9} /> : vPct < 0 ? <ArrowDownRight size={9} /> : <Minus size={9} />}
                              {vPct !== 0 ? `${vPct > 0 ? '+' : ''}${vPct.toFixed(2)}%` : 'No change'}
                            </span>
                          ) : (
                            <span className="text-stone-700 text-[10px]">—</span>
                          )}
                        </td>
                      )
                    })}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Variance reasons */}
          {lifecycleRecords.filter((r) => r.variance_reasons && r.variance_reasons.length > 0).length > 0 && (
            <div className="panel">
              <div className="panel-header">
                <span className="panel-header-title">Variance Attribution</span>
                <span className="text-[10px] text-stone-600">reasons for value changes by stage</span>
              </div>
              <div className="panel-body space-y-3">
                {lifecycleRecords.filter((r) => r.variance_reasons?.length).map((rec) => {
                  const stage = LIFECYCLE_STAGES.find((s) => s.id === rec.stage)
                  return (
                    <div key={rec.stage}>
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="text-[10px] font-medium text-amber-300">{stage?.label}</span>
                        <span className={`text-[10px] font-mono ${(rec.variance_pct ?? 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                          {(rec.variance_pct ?? 0) >= 0 ? '+' : ''}{rec.variance_pct?.toFixed(2)}%
                        </span>
                        <span className="text-[10px] text-stone-600">vs prior stage</span>
                      </div>
                      <div className="space-y-1">
                        {rec.variance_reasons!.map((reason, i) => (
                          <div key={i} className="flex items-center gap-2 text-[11px] text-stone-400">
                            <ChevronRight size={9} className="text-amber-400 shrink-0" />
                            {reason}
                          </div>
                        ))}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── QUEUE VIEW (existing) ─────────────────────────────────────── */}
      {valView === 'queue' && (<>

      {/* ── Metrics Strip ─────────────────────────────────────────────── */}
      <div className="grid grid-cols-4 gap-3">
        <MetricCard icon={<ClipboardCheck size={14} />} label="Properties Valued" value={String(propertiesValued)} />
        <MetricCard icon={<Percent size={14} />} label="Avg Cap Rate" value={withCapRate.length ? `${avgCapRate.toFixed(2)}%` : '—'} />
        <MetricCard
          icon={<TrendingUp size={14} />}
          label="Avg Value Change"
          value={
            recordsWithChange.length
              ? `${avgValueChange >= 0 ? '+' : ''}${avgValueChange.toFixed(1)}%`
              : '—'
          }
          valueColor={
            recordsWithChange.length
              ? avgValueChange >= 0 ? 'text-emerald-400' : 'text-red-400'
              : undefined
          }
        />
        <MetricCard icon={<Building2 size={14} />} label="Awaiting Review" value={String(awaitingReview)} />
      </div>

      {/* ── Valuation Queue ───────────────────────────────────────────── */}
      <div className="panel overflow-x-auto">
        <div className="panel-header">
          <span className="panel-header-title">Valuation Queue</span>
          <span className="text-[10px] text-stone-600 font-mono">{filteredRecords.length} properties</span>
        </div>
        <table className="dense-table">
          <thead>
            <tr>
              <th>Property</th>
              <th>Type</th>
              <th>Status</th>
              <th>Appraiser</th>
              <th className="text-right">As-Is Value</th>
              <th className="text-right">Stab Value</th>
              <th className="text-right">Cap Rate</th>
              <th className="text-right">Discount</th>
              <th className="text-right">Terminal</th>
              <th className="text-right">Occupancy</th>
              <th className="text-right">Prior Value</th>
              <th className="text-right">Change ($M)</th>
              <th className="text-right">Change (%)</th>
            </tr>
          </thead>
          <tbody>
            {filteredRecords.length === 0 ? (
              <tr>
                <td colSpan={13} className="py-8 text-center text-stone-500 text-[11px]">
                  {isLive
                    ? 'No live loans have a recorded property value or NOI to value.'
                    : 'No valuation records in this filter.'}
                </td>
              </tr>
            ) : null}
            {filteredRecords.map((v) => {
              const isSelected = v.propertyId === selectedId
              return (
                <tr
                  key={v.propertyId}
                  onClick={() => setSelectedId(v.propertyId)}
                  className={`cursor-pointer ${isSelected ? 'selected' : ''}`}
                >
                  <td className="text-stone-200 font-medium">
                    <div className="flex items-center gap-1">
                      {isSelected && <ChevronRight size={11} className="text-amber-400" />}
                      {v.propertyName}
                    </div>
                  </td>
                  <td>
                    <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-mono ${
                      (v.valuationType ?? 'post-close') === 'pre-deal'
                        ? 'bg-blue-400/10 text-blue-400 border border-blue-400/20'
                        : 'bg-emerald-400/10 text-emerald-400 border border-emerald-400/20'
                    }`}>
                      {(v.valuationType ?? 'post-close') === 'pre-deal' ? 'PRE' : 'POST'}
                    </span>
                  </td>
                  <td>
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium border ${statusBadge(v.status)}`}>
                      {v.status}
                    </span>
                  </td>
                  <td className="text-stone-400 text-[11px]">{v.appraiser || '—'}</td>
                  <td className="text-right tabular-nums" title={v.valueBasis}>
                    {money(v.asIsValue)}
                    {v.valueBasis && <span className="ml-1 text-[8px] font-medium uppercase tracking-wider text-violet-300">Derived</span>}
                  </td>
                  <td className="text-right tabular-nums">{money(v.stabilizedValue)}</td>
                  <td className="text-right tabular-nums">{rate(v.capRate)}</td>
                  <td className="text-right tabular-nums">{rate(v.discountRate)}</td>
                  <td className="text-right tabular-nums">{rate(v.terminalCapRate)}</td>
                  <td className="text-right tabular-nums">{share(v.occupancy, 1)}</td>
                  <td className="text-right tabular-nums">{v.priorValue != null ? fmt(v.priorValue) : '—'}</td>
                  <td className={`text-right tabular-nums ${(v.valueChange ?? 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {v.valueChange != null ? `${v.valueChange >= 0 ? '+' : ''}${v.valueChange.toFixed(1)}` : '—'}
                  </td>
                  <td className={`text-right tabular-nums ${(v.valueChangePct ?? 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {v.valueChangePct != null ? `${v.valueChangePct >= 0 ? '+' : ''}${v.valueChangePct.toFixed(1)}%` : '—'}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* ── Detail + Benchmarking ─────────────────────────────────────── */}
      {!selected ? (
        <div className="panel">
          <div className="panel-body py-10 text-center text-stone-500 text-[12px]">
            Select a valuation record, or add property value / NOI on a live loan.
          </div>
        </div>
      ) : (
      <div className="grid grid-cols-2 gap-3">
        {/* Left — Valuation Detail */}
        <div className="panel">
          <div className="panel-header">
            <span className="panel-header-title">Valuation Detail</span>
          </div>
          <div className="panel-body space-y-4">
            {/* Property identity */}
            <div>
              <div className="text-stone-200 font-semibold text-sm">{selected.propertyName}</div>
              <div className="text-[11px] text-stone-500">{deal?.location ?? 'N/A'} &middot; {deal?.type ?? ''}</div>
            </div>

            {/* Large values */}
            <div className="grid grid-cols-2 gap-3">
              <div className="metric-cell">
                <div className="metric-cell-label">As-Is Value{selected.valueBasis ? ' · Derived' : ''}</div>
                <div className="metric-cell-value text-amber-300">{money(selected.asIsValue)}</div>
                {selected.valueBasis && <div className="text-[10px] text-stone-500">{selected.valueBasis}</div>}
              </div>
              <div className="metric-cell">
                <div className="metric-cell-label">Stabilized Value</div>
                <div className="metric-cell-value">{money(selected.stabilizedValue)}</div>
              </div>
            </div>

            <div className="gold-divider" />

            {/* Key rates */}
            <div className="grid grid-cols-3 gap-3">
              <KV label="Cap Rate" value={rate(selected.capRate)} />
              <KV label="Discount Rate" value={rate(selected.discountRate)} />
              <KV label="Terminal Cap" value={rate(selected.terminalCapRate)} />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <KV label="NOI" value={selected.noi > 0 ? `$${selected.noi.toFixed(2)}M` : '—'} />
              <KV label="Occupancy" value={share(selected.occupancy, 1)} />
            </div>

            <div className="gold-divider" />

            {/* Value change */}
            {selected.valueChange != null && (
              <div className="grid grid-cols-2 gap-3">
                <KV
                  label="Value Change"
                  value={`${selected.valueChange >= 0 ? '+' : ''}$${Math.abs(selected.valueChange).toFixed(1)}M`}
                  color={selected.valueChange >= 0 ? 'text-emerald-400' : 'text-red-400'}
                />
                <KV
                  label="% Change"
                  value={`${(selected.valueChangePct ?? 0) >= 0 ? '+' : ''}${(selected.valueChangePct ?? 0).toFixed(1)}%`}
                  color={(selected.valueChangePct ?? 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}
                />
              </div>
            )}

            <div className="gold-divider" />

            {/* Yield Rates */}
            <div>
              <div className="text-[9px] font-mono uppercase tracking-widest text-stone-600 mb-2">Yield Rates</div>
              <div className="grid grid-cols-3 gap-3">
                <KV label="Implied Cap" value={rate(selected.capRate)} />
                <KV label="Terminal Cap" value={rate(selected.terminalCapRate)} />
                <KV label="Discount Rate" value={rate(selected.discountRate)} />
              </div>
            </div>

            <div className="gold-divider" />

            {/* Income Approach — demo-only synthetic approaches, never live */}
            {demoEnabled && (
            <div>
              <div className="text-[9px] font-mono uppercase tracking-widest text-stone-600 mb-2">Income Approach</div>
              <div className="grid grid-cols-2 gap-3">
                <KV label="Direct Cap Value" value={fmt(selected.asIsValue * 1.02)} />
                <KV label="DCF Value" value={fmt(selected.asIsValue * 0.98)} />
              </div>
            </div>
            )}
          </div>
        </div>

        {/* Right — Benchmarking + Performance (curated demo series only) */}
        <div className="space-y-3">
          {!demoEnabled && (
            <div className="panel">
              <div className="panel-body py-10 text-center text-stone-500 text-[12px]">
                No peer-group benchmarks are on file for the live book.
              </div>
            </div>
          )}
          {demoEnabled && <div className="panel">
            <div className="panel-header">
              <span className="panel-header-title">Benchmarking</span>
              <span className="text-[10px] text-stone-600 font-mono">vs Peer Group</span>
            </div>
            <div className="panel-body space-y-4">
              {/* Cap Rate Comparison */}
              <div>
                <div className="text-[9px] font-mono uppercase tracking-widest text-stone-600 mb-1.5">Cap Rate Comparison (%)</div>
                <div className="h-[100px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={capRateBench} layout="vertical" margin={{ left: 4, right: 16, top: 2, bottom: 2 }}>
                      <CartesianGrid {...chartGridProps(false)} />
                      <XAxis type="number" {...chartAxisProps(9)} domain={['dataMin - 0.5', 'dataMax + 0.5']} />
                      <YAxis type="category" dataKey="name" {...chartAxisProps(9)} width={66} />
                      <Tooltip {...CHART_TOOLTIP_PROPS} formatter={(v: number) => [`${v.toFixed(2)}%`]} />
                      <Bar dataKey="value" radius={[0, 3, 3, 0]} barSize={14}>
                        {capRateBench.map((_, i) => (
                          <Cell key={i} fill={chartBenchmarkBarColor(i === 0, 4)} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Value/SF Comparison */}
              <div>
                <div className="text-[9px] font-mono uppercase tracking-widest text-stone-600 mb-1.5">Value per SF ($/SF)</div>
                <div className="h-[100px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={valueSfBench} layout="vertical" margin={{ left: 4, right: 16, top: 2, bottom: 2 }}>
                      <CartesianGrid {...chartGridProps(false)} />
                      <XAxis type="number" {...chartAxisProps(9)} />
                      <YAxis type="category" dataKey="name" {...chartAxisProps(9)} width={66} />
                      <Tooltip {...CHART_TOOLTIP_PROPS} formatter={(v: number) => [`$${v.toFixed(0)}`]} />
                      <Bar dataKey="value" radius={[0, 3, 3, 0]} barSize={14}>
                        {valueSfBench.map((_, i) => (
                          <Cell key={i} fill={chartBenchmarkBarColor(i === 0, 4)} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Occupancy Comparison */}
              <div>
                <div className="text-[9px] font-mono uppercase tracking-widest text-stone-600 mb-1.5">Occupancy (%)</div>
                <div className="h-[80px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={occBench} layout="vertical" margin={{ left: 4, right: 16, top: 2, bottom: 2 }}>
                      <CartesianGrid {...chartGridProps(false)} />
                      <XAxis type="number" {...chartAxisProps(9)} domain={[80, 100]} />
                      <YAxis type="category" dataKey="name" {...chartAxisProps(9)} width={66} />
                      <Tooltip {...CHART_TOOLTIP_PROPS} formatter={(v: number) => [`${v.toFixed(1)}%`]} />
                      <Bar dataKey="value" radius={[0, 3, 3, 0]} barSize={14}>
                        {occBench.map((_, i) => (
                          <Cell key={i} fill={chartBenchmarkBarColor(i === 0, 4)} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </div>}

          {/* Performance Attribution — curated demo series only */}
          {demoEnabled && <div className="panel">
            <div className="panel-header">
              <span className="panel-header-title">Performance Attribution</span>
            </div>
            <div className="panel-body space-y-2">
              {ATTRIBUTION.map((a, i) => (
                <div key={i} className="flex items-center justify-between">
                  <span className="text-[11px] text-stone-400">{a.label}</span>
                  <div className="flex items-center gap-2">
                    <div className="chart-viz-track chart-viz-bar--rounded h-1.5 w-24">
                      <div
                        className="chart-viz-bar chart-viz-bar--rounded h-full"
                        style={{
                          width: `${Math.min(Math.abs(a.value) / 3 * 100, 100)}%`,
                          marginLeft: a.value < 0 ? 'auto' : undefined,
                          background: chartPassFailColor(a.value >= 0),
                        }}
                      />
                    </div>
                    <span className={`text-[11px] tabular-nums font-medium w-12 text-right ${a.value >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {a.value >= 0 ? '+' : ''}{a.value.toFixed(1)}%
                    </span>
                  </div>
                </div>
              ))}
              <div className="gold-divider my-1" />
              <div className="flex items-center justify-between">
                <span className="text-[11px] text-amber-300 font-semibold">Total Return</span>
                <span className={`text-[12px] tabular-nums font-bold ${totalReturn >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  {totalReturn >= 0 ? '+' : ''}{totalReturn.toFixed(1)}%
                </span>
              </div>
            </div>
          </div>}
        </div>
      </div>
      )}
      </>)}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function MetricCard({
  icon,
  label,
  value,
  valueColor,
}: {
  icon: React.ReactNode
  label: string
  value: string
  valueColor?: string
}) {
  return (
    <div className="metric-cell">
      <div className="flex items-center gap-1.5 metric-cell-label">
        {icon}
        {label}
      </div>
      <div className={`metric-cell-value ${valueColor ?? ''}`}>{value}</div>
    </div>
  )
}

function KV({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div>
      <div className="text-[9px] font-mono uppercase tracking-widest text-stone-600">{label}</div>
      <div className={`text-sm font-semibold tabular-nums ${color ?? 'text-stone-200'}`}>{value}</div>
    </div>
  )
}
